import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Mail, Calendar, Users, Send, Clock, Loader2, X, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function BudgetEmailScheduler({ 
  isOpen, 
  onClose, 
  condoId, 
  yearId, 
  docType,
  selectedDocs,
  availableDocs,
  token 
}) {
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [sendNow, setSendNow] = useState(false);
  
  // Load recipients when dialog opens
  useEffect(() => {
    if (isOpen && condoId) {
      loadRecipients();
      // Set default subject
      const typeLabel = docType === 'PREVENTIVO' ? 'Preventivo' : 'Consuntivo';
      setEmailSubject(`Documentazione Bilancio ${typeLabel}`);
      // Set default date to tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setScheduledDate(tomorrow.toISOString().split('T')[0]);
    }
  }, [isOpen, condoId, docType]);
  
  const loadRecipients = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/residents-for-email`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setRecipients(data.recipients);
        // Pre-select all owners
        const owners = data.recipients.filter(r => r.is_owner).map(r => r.id);
        setSelectedRecipients(owners);
      }
    } catch (error) {
      toast.error('Errore caricamento destinatari');
    } finally {
      setLoading(false);
    }
  };
  
  const toggleRecipient = (recipientId) => {
    setSelectedRecipients(prev =>
      prev.includes(recipientId)
        ? prev.filter(id => id !== recipientId)
        : [...prev, recipientId]
    );
  };
  
  const selectAllRecipients = () => {
    setSelectedRecipients(recipients.map(r => r.id));
  };
  
  const selectOwners = () => {
    setSelectedRecipients(recipients.filter(r => r.is_owner).map(r => r.id));
  };
  
  const handleSchedule = async () => {
    if (selectedRecipients.length === 0) {
      toast.error('Seleziona almeno un destinatario');
      return;
    }
    
    if (!emailSubject.trim()) {
      toast.error('Inserisci un oggetto per l\'email');
      return;
    }
    
    if (!sendNow && !scheduledDate) {
      toast.error('Seleziona una data di invio');
      return;
    }
    
    setSending(true);
    
    try {
      // Build scheduled datetime
      let scheduledAt;
      if (sendNow) {
        scheduledAt = new Date().toISOString();
      } else {
        scheduledAt = new Date(`${scheduledDate}T${scheduledTime}:00`).toISOString();
      }
      
      // Build recipients list
      const recipientsList = recipients
        .filter(r => selectedRecipients.includes(r.id))
        .map(r => ({ email: r.email, name: r.name }));
      
      const endpoint = sendNow 
        ? `${API_URL}/api/condominiums/${condoId}/budget-email-schedules/send-now`
        : `${API_URL}/api/condominiums/${condoId}/budget-email-schedules`;
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          year_id: yearId,
          doc_type: docType,
          selected_docs: selectedDocs,
          recipients: recipientsList,
          scheduled_at: scheduledAt,
          email_subject: emailSubject,
          email_message: emailMessage
        })
      });
      
      if (res.ok) {
        const result = await res.json();
        if (sendNow) {
          toast.success(`Email inviate: ${result.sent} su ${result.total}`);
          if (result.failed > 0) {
            toast.warning(`${result.failed} email non inviate`);
          }
        } else {
          toast.success('Invio programmato con successo');
        }
        onClose();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore programmazione invio');
      }
    } catch (error) {
      toast.error('Errore programmazione invio');
    } finally {
      setSending(false);
    }
  };
  
  const getSelectedDocsNames = () => {
    return selectedDocs
      .map(docId => availableDocs.find(d => d.id === docId)?.name)
      .filter(Boolean)
      .join(', ');
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Programma Invio Email
          </DialogTitle>
          <DialogDescription>
            Invia la documentazione budget ai condomini via email.
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 flex-1 overflow-y-auto">
            {/* Documents summary */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <Label className="text-sm font-medium">Documenti da inviare</Label>
              <p className="text-sm text-muted-foreground mt-1">
                {selectedDocs.length} documenti: {getSelectedDocsNames()}
              </p>
            </div>
            
            {/* Email subject */}
            <div className="space-y-2">
              <Label htmlFor="subject">Oggetto email *</Label>
              <Input
                id="subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Documentazione Bilancio Preventivo 2025"
                data-testid="email-subject-input"
              />
            </div>
            
            {/* Email message */}
            <div className="space-y-2">
              <Label htmlFor="message">Messaggio personalizzato (opzionale)</Label>
              <Textarea
                id="message"
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Aggiungi un messaggio personalizzato per i destinatari..."
                rows={3}
                data-testid="email-message-input"
              />
            </div>
            
            {/* Schedule options */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Quando inviare</Label>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="sendNow"
                  checked={sendNow}
                  onCheckedChange={setSendNow}
                />
                <Label htmlFor="sendNow" className="text-sm cursor-pointer">
                  Invia subito
                </Label>
              </div>
              
              {!sendNow && (
                <div className="flex gap-3 items-center">
                  <div className="flex-1">
                    <Label htmlFor="date" className="text-xs text-muted-foreground">Data</Label>
                    <Input
                      id="date"
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      data-testid="schedule-date-input"
                    />
                  </div>
                  <div className="w-32">
                    <Label htmlFor="time" className="text-xs text-muted-foreground">Ora</Label>
                    <Input
                      id="time"
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      data-testid="schedule-time-input"
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Recipients */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Destinatari ({selectedRecipients.length}/{recipients.length})
                </Label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={selectAllRecipients}>
                    Tutti
                  </Button>
                  <Button size="sm" variant="outline" onClick={selectOwners}>
                    Solo Proprietari
                  </Button>
                </div>
              </div>
              
              <div className="max-h-[200px] overflow-y-auto space-y-1 border rounded-lg p-2">
                {recipients.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-2">
                    Nessun residente con email trovato
                  </p>
                ) : (
                  recipients.map((recipient) => (
                    <div
                      key={recipient.id}
                      className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                        selectedRecipients.includes(recipient.id) ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => toggleRecipient(recipient.id)}
                      data-testid={`recipient-${recipient.id}`}
                    >
                      <Checkbox
                        checked={selectedRecipients.includes(recipient.id)}
                        onCheckedChange={() => toggleRecipient(recipient.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{recipient.name}</span>
                          {recipient.is_owner && (
                            <Badge variant="secondary" className="text-xs">Proprietario</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {recipient.email} • Unità {recipient.unit_number}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="cancel-schedule">
            Annulla
          </Button>
          <Button
            onClick={handleSchedule}
            disabled={sending || selectedRecipients.length === 0}
            className={sendNow ? 'bg-orange-600 hover:bg-orange-700' : 'bg-blue-600 hover:bg-blue-700'}
            data-testid="confirm-schedule"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : sendNow ? (
              <Send className="h-4 w-4 mr-2" />
            ) : (
              <Clock className="h-4 w-4 mr-2" />
            )}
            {sendNow ? `Invia Ora (${selectedRecipients.length})` : `Programma Invio (${selectedRecipients.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BudgetEmailScheduler;
